// Shared types + constants used by both the tenant Support pages and
// the super-admin Tickets pages. Keep this aligned with backend schema.

export type TicketStatus = 'open' | 'in_progress' | 'pending' | 'on_hold' | 'resolved' | 'closed';
export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';
export type TicketScope = 'tenant_internal' | 'platform' | 'platform_internal';

export interface Ticket {
  id: string;
  scope: TicketScope;
  tenantId: string;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  subject: string;
  message: string;
  category: string;
  priority: TicketPriority;
  status: TicketStatus;
  reply: string | null;
  repliedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Super-admin assignment (used for scope=platform, platform_internal)
  assignedAdminId?: string | null;
  assignedAdminName?: string | null;
  // Tenant-side assignment (used for scope=tenant_internal)
  assignedTenantUserId?: string | null;
  assignedTenantUserName?: string | null;
}

export interface ThreadMessage {
  id: string;
  ticketId: string;
  tenantId: string;
  authorType: 'tenant' | 'admin' | 'system';
  authorName: string | null;
  authorEmail: string | null;
  body: string;
  isInternal: boolean;
  createdAt: string;
}

export const STATUS_COLORS: Record<TicketStatus, string> = {
  open: 'bg-blue-50 text-blue-700 border-blue-100',
  in_progress: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  pending: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  on_hold: 'bg-amber-50 text-amber-700 border-amber-100',
  resolved: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  closed: 'bg-slate-100 text-slate-600 border-slate-200',
};

export const PRIORITY_COLORS: Record<TicketPriority, string> = {
  low: 'bg-slate-100 text-slate-600',
  normal: 'bg-blue-50 text-blue-700',
  high: 'bg-orange-50 text-orange-700',
  urgent: 'bg-red-50 text-red-700',
};

export const CATEGORIES = [
  { v: 'general',         l: 'General Question' },
  { v: 'billing',         l: 'Billing' },
  { v: 'bug',             l: 'Bug Report' },
  { v: 'feature_request', l: 'Feature Request' },
  { v: 'integration',     l: 'Integration Help' },
  { v: 'other',           l: 'Other' },
];

export const STATUS_OPTIONS: TicketStatus[] = [
  'open',
  'in_progress',
  'on_hold',
  'resolved',
  'closed',
];

export const PRIORITY_OPTIONS: TicketPriority[] = ['low', 'normal', 'high', 'urgent'];

export const SCOPE_LABELS: Record<TicketScope, { label: string; short: string; color: string }> = {
  tenant_internal: {
    label: 'Internal Team',
    short: 'Internal',
    color: 'bg-violet-50 text-violet-700 border-violet-100',
  },
  platform: {
    label: 'BrixLead Support',
    short: 'BrixLead',
    color: 'bg-sky-50 text-sky-700 border-sky-100',
  },
  platform_internal: {
    label: 'Platform Internal',
    short: 'Platform',
    color: 'bg-purple-50 text-purple-700 border-purple-100',
  },
};

export function humanStatus(s: TicketStatus | string): string {
  const map: Record<string, string> = {
    open: 'Open',
    in_progress: 'In Progress',
    pending: 'In Progress',
    waiting: 'On Hold',
    on_hold: 'On Hold',
    resolved: 'Resolved',
    closed: 'Closed',
  };
  return map[s] ?? s;
}
